import { Attachment } from '@/assets/images';
import { getAcceptString } from '@/features/chats/sub-features/media/utils/fileTypeUtils';
import { useMediaFile } from '@/features/chats/sub-features/media/viewmodel/useMediaFile';
import { attachmentItems } from '@/features/chats/utils/attachmentUtils.jsx';
import useKeyboardNavigation from '@/shared/hooks/useKeyboardNavigation';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import './AttachmentMenu.scss';

const resolvePhotoVideoItem = (item, isImageEnabled, isVideoEnabled) => {
   if (item.label !== 'Photos & Videos') return item;
   if (isImageEnabled && !isVideoEnabled)
      return { ...item, label: 'Photos', accept: getAcceptString('image'), mediaType: 'image' };
   if (!isImageEnabled && isVideoEnabled)
      return { ...item, label: 'Videos', accept: getAcceptString('video'), mediaType: 'video' };
   return item;
};

const AttachmentMenu = ({ activeChatId, position = 'top' }) => {
   const [isMenuOpen, setIsMenuOpen] = useState(false);
   const fileInputRef = useRef(null);

   const {
      openMediaPreview,
      mediaFeatures: {
         media: isAttachmentEnabled,
         photo: isImageEnabled,
         video: isVideoEnabled,
         audio: isAudioEnabled,
         document: isDocumentEnabled,
         contact: isContactEnabled,
         location: isLocationEnabled,
      } = {},
   } = useMediaFile({ activeChatId });

   const closeMenu = useCallback(() => setIsMenuOpen(false), []);

   const keyboardConfig = useMemo(() => ({ Escape: closeMenu }), [closeMenu]);
   useKeyboardNavigation(keyboardConfig, { enabled: isMenuOpen });

   const triggerFileInput = useCallback(
      ({ accept, mediaType, onFileChange }) => {
         if (fileInputRef.current && accept) {
            Object.assign(fileInputRef.current, { accept, mediaType });
            fileInputRef.current.click();
         } else {
            onFileChange?.(activeChatId);
         }
      },
      [activeChatId],
   );

   const handleFileSelected = useCallback(
      e => {
         const selectedFiles = Array.from(e.target.files).filter(Boolean);
         if (selectedFiles.length > 0) openMediaPreview(selectedFiles, fileInputRef.current.mediaType);
         e.target.value = '';
      },
      [openMediaPreview],
   );

   const visibleAttachmentItems = useMemo(() => {
      const visibilityMap = {
         Camera: isImageEnabled,
         Location: isLocationEnabled,
         'Photos & Videos': isImageEnabled || isVideoEnabled,
         Audio: isAudioEnabled,
         Contact: isContactEnabled,
         Document: isDocumentEnabled,
      };
      return attachmentItems?.filter(({ label }) => visibilityMap[label] ?? true) ?? [];
   }, [isAudioEnabled, isDocumentEnabled, isImageEnabled, isVideoEnabled, isContactEnabled, isLocationEnabled]);

   const handleAttachmentSelect = useCallback(
      item => {
         triggerFileInput(item);
         closeMenu();
      },
      [triggerFileInput, closeMenu],
   );

   if (!isAttachmentEnabled) return null;

   return (
      <div className="attachment-container ct-relative ct-flex">
         <button
            className={`attachment-toggle ct-flexCenter ct-cursor ${isMenuOpen ? 'active' : ''}`}
            onClick={() => setIsMenuOpen(prev => !prev)}
            aria-label="Toggle attachments"
         >
            <i className="ct-flexCenter">
               <Attachment />
            </i>
         </button>

         <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileSelected} multiple />

         {isMenuOpen && (
            <>
               <button className="attachment-overlay" onClick={closeMenu} aria-label="Close attachments" />
               <div className={`attachment-menu fsd-modern-menu ct-flex ct-fcol ${position}`}>
                  {visibleAttachmentItems.map(item => {
                     const resolvedItem = resolvePhotoVideoItem(item, isImageEnabled, isVideoEnabled);
                     return (
                        <button
                           key={item.label}
                           className="attachment-item fsd-modern-item ct-flex ct-aiCenter ct-cursor ct-gap12"
                           onClick={() => handleAttachmentSelect(resolvedItem)}
                           aria-label={`Attach ${resolvedItem.label}`}
                        >
                           <div className="icon-wrapper ct-flexCenter" style={{ color: item.color }}>
                              <i className="ct-icon">{item.icon}</i>
                           </div>
                           <div className="label-text">{resolvedItem.label}</div>
                        </button>
                     );
                  })}
               </div>
            </>
         )}
      </div>
   );
};

AttachmentMenu.propTypes = {
   activeChatId: PropTypes.string,
   position: PropTypes.string,
};

export default React.memo(AttachmentMenu);
